import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseConfigService } from '@fuse/services/config.service';
import { select, Store } from '@ngrx/store';
import { CompanySubset } from 'app/api/company.api.service';
import { User } from 'app/api/user.api.service';
import { AuthService } from 'app/auth/auth.service';
import { CompaniesUIActions } from 'app/main/company/actions';
import { Period } from 'app/main/company/company.types';
import { CompaniesSelectors, CompaniesUISelectors } from 'app/main/company/selectors';
import { defaultNavigation } from 'app/navigation/navigation';
import { AppState } from 'app/reducers';
import { AmplifyService } from 'aws-amplify-angular';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'toolbar',
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class ToolbarComponent implements OnInit, OnDestroy {
    horizontalNavbar: boolean;
    rightNavbar: boolean;
    hiddenNavbar: boolean;
    navigation: any;
    currentUser: User;

    currentCompany$: Observable<CompanySubset>;
    companies$: Observable<CompanySubset[]>;
    currentPeriod$: Observable<Period>;
    periods$: Observable<Period[]>;


    // Private
    private _unsubscribeAll: Subject<any>;

    constructor(
        protected amplifyService: AmplifyService,
        protected auth: AuthService,
        protected store: Store<AppState>,
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        private _fuseConfigService: FuseConfigService,
        private _fuseSidebarService: FuseSidebarService,
    ) {
        this.navigation = defaultNavigation;

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Get current user
        this.auth.onCurrentUserChanged
            .pipe(
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this.currentUser = this.auth.currentUser;
            });

        this.companies$ = this.store.pipe(select(CompaniesSelectors.selectAllCompaniesSorted));
        this.currentCompany$ = this.store.pipe(select(CompaniesSelectors.selectCurrentCompany));
        this.periods$ = this.store.pipe(select(CompaniesSelectors.selectCurrentCompanyPeriods));
        this.currentPeriod$ = this.store.pipe(select(CompaniesUISelectors.selectCurrentPeriod));

        // Subscribe to the config changes
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((settings) => {
                this.horizontalNavbar = settings.layout.navbar.position === 'top';
                this.rightNavbar = settings.layout.navbar.position === 'right';
                this.hiddenNavbar = settings.layout.navbar.hidden === true;
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }

    setCompany(current: CompanySubset): void {
        this.store.dispatch(CompaniesUIActions.setCurrentCompany({ companyId: current.id }));
    }

    setPeriod(period: Period): void {
        this.store.dispatch(CompaniesUIActions.setCurrentPeriod({ period }));
    }

    createCompany(): void {
        // Not implemented
    }

    logout(): void {
        this.amplifyService.auth().signOut({ global: true });
    }

    currentUserName(): string {
        if (!this.currentUser) {
            return '';
        }
        return `${this.currentUser.firstname} ${this.currentUser.lastname}`;
    }

}
